/**
 * Schemes that can execute or smuggle a payload. Mirrors the denylist in
 * RuleCMS `text-safety/validate-text-content.ts`; the two must stay in step,
 * because that one warns at save and this one is the guarantee at render.
 */
export const REJECTED_HREF_SCHEMES: readonly string[] = [
  'javascript',
  // Netscape-era aliases for javascript:. No browser in use today runs them,
  // but RuleCMS refuses them in prose, and an href accepting what the text
  // field rejects is the kind of gap that outlives the reason for it.
  'livescript',
  'mocha',
  'vbscript',
  'data',
  'file',
  'blob',
];

const SCHEME = /^([a-z][a-z0-9+.-]*):/;

/**
 * Characters a browser ignores while reading a scheme. Stripped before the
 * scheme is matched so `java\tscript:` and a leading NUL cannot hide one.
 */
const isIgnoredWhenReadingAScheme = (charCode: number): boolean =>
  charCode <= 0x20 || charCode === 0x7f;

const schemeProbe = (value: string): string => {
  let probe = '';
  for (let index = 0; index < value.length; index += 1) {
    if (!isIgnoredWhenReadingAScheme(value.charCodeAt(index))) {
      probe += value[index];
    }
  }
  return probe.toLowerCase();
};

/**
 * Whether a value is safe to put in an `href`.
 *
 * A denylist, not an allowlist. Customers link with git and ssh URLs, mobile
 * deep links, `mailto:`, `tel:`, `sms:`, custom app schemes, protocol-relative
 * `//host/path`, and bare `/path`, `#anchor`, `?q=1`, `./rel` — all of which
 * are accepted. A host is not required and neither is http/https. Only the
 * schemes that turn a link into code or an opaque payload are turned away.
 *
 * Rejection happens at render, never at save: the composer may use this for a
 * non-blocking warning, but stored content is not trusted content.
 */
export function isSafeHref(value: unknown): boolean {
  if (typeof value !== 'string' || value.trim() === '') {
    return false;
  }

  const match = SCHEME.exec(schemeProbe(value));
  // No scheme at all: relative, fragment, query, or protocol-relative.
  if (!match) {
    return true;
  }

  return !REJECTED_HREF_SCHEMES.includes(match[1]);
}
